import { useState, useEffect, useRef } from 'react';
import { reportsApi, type ReportJob } from '../api/client';
import { T } from '../components/ui';

const REPORT_TYPES = [
 {
 id: 'fsma_204',
 icon: '🏛',
 title: 'FDA FSMA 204',
 subtitle: 'Traceability Record',
 desc: 'Full supply chain traceability documentation meeting FDA Section 204 requirements. Includes provenance chains, supplier records, and lot-level tracking.',
 scope_fields: ['retailer_id', 'product_id', 'date_from', 'date_to'],
 },
 {
 id: 'eudr',
 icon: '🌿',
 title: 'EU Deforestation',
 subtitle: 'EUDR Compliance',
 desc: 'EU Deforestation Regulation compliance assessment. Maps supply chain origins, deforestation risk by country, and certification coverage.',
 scope_fields: ['supplier_id', 'date_from', 'date_to'],
 },
 {
 id: 'supplier_risk',
 icon: '⚠',
 title: 'Supplier Risk',
 subtitle: 'Risk Summary',
 desc: 'Ranked supplier risk analysis covering alert history, certification gaps, recall exposure, and recommended interventions.',
 scope_fields: ['supplier_id', 'date_from', 'date_to'],
 },
 {
 id: 'recall_scope',
 icon: '🚨',
 title: 'Recall Scope',
 subtitle: 'Documentation',
 desc: 'Official recall scope documentation for regulatory submission. Includes affected products, distribution scope, and remediation steps.',
 scope_fields: ['recall_id'],
 required: ['recall_id'],
 },
];

const STATUS_CONFIG: Record<string, { color: string; label: string; pulse?: boolean }> = {
 queued: { color: '#d4a847', label: 'Queued' },
 generating: { color: '#5b9bd5', label: 'Generating', pulse: true },
 done: { color: '#7eb89a', label: 'Ready' },
 failed: { color: T.crimson, label: 'Failed' },
};

const STYLES = {
 page: { padding: '24px', maxWidth: 1100 },
 heading: { fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 4 },
 sub: { fontSize: 13, color: T.sub, marginBottom: 28 },
 card: { background: T.panel, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, marginBottom: 16 },
 label: { fontSize: 11, color: T.sub, textTransform: 'uppercase' as const, letterSpacing: '0.5px', marginBottom: 6 },
 input: {
 width: '100%', padding: '10px 14px', background: T.surface,
 border: `1px solid ${T.border}`, borderRadius: 8, color: T.text,
 fontSize: 13, outline: 'none', boxSizing: 'border-box' as const,
 },
 btn: (v: 'primary' | 'secondary' | 'ghost' | 'danger' = 'secondary'): React.CSSProperties => ({
 padding: '9px 18px', borderRadius: 8, cursor: 'pointer',
 fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' as const,
 background: v === 'primary' ? '#e8935a' : v === 'danger' ? '#3d1010' : v === 'ghost' ? 'transparent' : '#1a1815',
 color: v === 'primary' ? '#fff' : v === 'danger' ? '#c0454a' : '#f0ebe4',
 border: v === 'secondary' ? `1px solid ${T.border}` : v === 'danger' ? `1px solid ${T.crimson}` : 'none',
 transition: 'all 0.12s',
 }),
 empty: { textAlign: 'center' as const, padding: '40px 0', color: T.dim, fontSize: 13 },
};

function ProgressBar({ pct, color }: { pct: number; color: string }) {
 return (
 <div style={{ height: 4, background: '#2a2620', borderRadius: 2, overflow: 'hidden' }}>
 <div style={{
 height: '100%', width: `${pct}%`, background: color,
 borderRadius: 2, transition: 'width 0.4s ease',
 }} />
 </div>
 );
}

function StatusDot({ status }: { status: string }) {
 const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.queued;
 return (
 <span style={{
 display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
 background: cfg.color, marginRight: 6,
 animation: cfg.pulse ? 'pulse 1.5s ease-in-out infinite' : 'none',
 }} />
 );
}

function formatBytes(n: number | null) {
 if (!n) return '—';
 if (n < 1024) return `${n}B`;
 if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
 return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

function timeAgo(iso: string | null) {
 if (!iso) return '—';
 const d = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
 if (d < 60) return 'just now';
 if (d < 3600) return `${Math.floor(d / 60)}m ago`;
 if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
 return new Date(iso).toLocaleDateString();
}

// ── New Report Form ───────────────────────────────────────────────────────────
function NewReportForm({ onCreated }: { onCreated: () => void }) {
 const [step, setStep] = useState<'pick' | 'config'>('pick');
 const [selectedType, setType] = useState<typeof REPORT_TYPES[number] | null>(null);
 const [title, setTitle] = useState('');
 const [scope, setScope] = useState<Record<string, string>>({});
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');

 const select = (t: typeof REPORT_TYPES[number]) => {
 setType(t);
 setTitle(t.title + ' — ' + t.subtitle);
 setScope({});
 setError('');
 setStep('config');
 };

 const submit = async () => {
 if (!selectedType) return;
 // Validate required fields
 for (const field of selectedType.required || []) {
 if (!scope[field]?.trim()) {
 setError(`${field.replace('_', ' ')} is required for this report type`);
 return;
 }
 }
 setLoading(true); setError('');
 try {
 // Filter out empty scope values
 const cleanScope = Object.fromEntries(
 Object.entries(scope).filter(([, v]) => (v as string)?.trim())
 );
 await reportsApi.create({ report_type: selectedType.id, title: title.trim() || undefined, scope: cleanScope });
 setStep('pick'); setType(null); setScope({});
 onCreated();
 } catch (e: any) {
 setError(e.message || 'Failed to queue report');
 } finally { setLoading(false); }
 };

 if (step === 'pick') {
 return (
 <div style={STYLES.card}>
 <div style={{ ...STYLES.label, marginBottom: 14 }}>Select Report Type</div>
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
 {REPORT_TYPES.map(t => (
 <button key={t.id} onClick={() => select(t)} style={{
 background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12,
 padding: 16, textAlign: 'left', cursor: 'pointer', transition: 'all 0.12s',
 }}
 onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#e8935a'; }}
 onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#2a2620'; }}
 >
 <div style={{ fontSize: 28, marginBottom: 8 }}>{t.icon}</div>
 <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 2 }}>{t.title}</div>
 <div style={{ fontSize: 11, color: T.sub, marginBottom: 8 }}>{t.subtitle}</div>
 <div style={{ fontSize: 11, color: T.dim, lineHeight: 1.4 }}>{t.desc.slice(0, 80)}…</div>
 </button>
 ))}
 </div>
 </div>
 );
 }

 // Config step
 const FIELD_LABELS: Record<string, { label: string; placeholder: string }> = {
 supplier_id: { label: 'Supplier ID (UUID)', placeholder: 'Leave empty for all suppliers' },
 retailer_id: { label: 'Retailer ID (UUID)', placeholder: 'Leave empty for all retailers' },
 product_id: { label: 'Product ID (UUID)', placeholder: 'Leave empty for all products' },
 recall_id: { label: 'Recall ID (UUID) *', placeholder: 'Required — paste the recall UUID' },
 date_from: { label: 'Date From', placeholder: 'YYYY-MM-DD (default: 90 days ago)' },
 date_to: { label: 'Date To', placeholder: 'YYYY-MM-DD (default: today)' },
 };

 return (
 <div style={STYLES.card}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
 <div>
 <span style={{ fontSize: 24, marginRight: 10 }}>{selectedType!.icon}</span>
 <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{selectedType!.title}</span>
 <span style={{ fontSize: 13, color: T.sub, marginLeft: 8 }}>{selectedType!.subtitle}</span>
 </div>
 <button onClick={() => setStep('pick')} style={STYLES.btn('ghost')}>← Back</button>
 </div>

 <div style={{ marginBottom: 16 }}>
 <div style={STYLES.label}>Report Title (optional)</div>
 <input
 value={title}
 onChange={e => setTitle(e.target.value)}
 placeholder="Custom title or leave as default"
 style={STYLES.input}
 />
 </div>

 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
 {selectedType!.scope_fields.map(field => {
 const { label, placeholder } = FIELD_LABELS[field] || { label: field, placeholder: '' };
 return (
 <div key={field}>
 <div style={STYLES.label}>{label}</div>
 <input
 value={scope[field] || ''}
 onChange={e => setScope(s => ({ ...s, [field]: e.target.value }))}
 placeholder={placeholder}
 type={field.endsWith('_id') ? 'text' : 'date'}
 style={STYLES.input}
 />
 </div>
 );
 })}
 </div>

 {error && <div style={{ color: T.crimson, fontSize: 13, marginBottom: 12 }}>{error}</div>}

 <div style={{ display: 'flex', gap: 10 }}>
 <button onClick={submit} disabled={loading} style={STYLES.btn('primary')}>
 {loading ? '⏳ Queuing…' : '📄 Generate Report'}
 </button>
 <button onClick={() => setStep('pick')} style={STYLES.btn('secondary')}>Cancel</button>
 </div>
 </div>
 );
}

// ── Report Job Row ────────────────────────────────────────────────────────────
function ReportRow({ job, onDelete }: { key?: React.Key; job: ReportJob; onDelete: () => void | Promise<void> }) {
 const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.queued;
 const canDownload = job.status === 'done';
 const canDelete = job.status !== 'generating';

 return (
 <div style={{
 padding: '16px 20px', borderBottom: '1px solid #1a1815',
 transition: 'background 0.1s',
 }}
 onMouseEnter={e => (e.currentTarget.style.background = '#141310')}
 onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
 >
 <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
 {/* Icon */}
 <div style={{ fontSize: 24, marginTop: 2 }}>
 {REPORT_TYPES.find(t => t.id === job.report_type)?.icon || '📄'}
 </div>

 {/* Info */}
 <div style={{ flex: 1, minWidth: 0 }}>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 2 }}>
 {job.title}
 </div>
 <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
 <span style={{ fontSize: 11, color: T.sub }}>
 <StatusDot status={job.status} />{cfg.label}
 </span>
 <span style={{ fontSize: 11, color: T.dim }}>Queued {timeAgo(job.queued_at)}</span>
 {job.completed_at && (
 <span style={{ fontSize: 11, color: T.dim }}>Done {timeAgo(job.completed_at)}</span>
 )}
 {job.file_size_bytes && (
 <span style={{ fontSize: 11, color: T.dim }}>{formatBytes(job.file_size_bytes)}</span>
 )}
 {job.created_by_name && (
 <span style={{ fontSize: 11, color: T.dim }}>by {job.created_by_name}</span>
 )}
 </div>

 {/* Progress bar for in-progress */}
 {job.status === 'generating' && (
 <div style={{ marginBottom: 8 }}>
 <ProgressBar pct={job.progress_pct} color="#5b9bd5" />
 <div style={{ fontSize: 11, color: '#5b9bd5', marginTop: 4 }}>{job.progress_pct}% complete</div>
 </div>
 )}

 {job.error_message && (
 <div style={{ fontSize: 12, color: T.crimson, marginTop: 4, padding: '6px 10px', background: '#1a0808', borderRadius: 6 }}>
 {job.error_message}
 </div>
 )}

 {/* Expiry warning */}
 {job.status === 'done' && job.expires_at && (
 <div style={{ fontSize: 10, color: T.dim, marginTop: 4 }}>
 Expires {new Date(job.expires_at).toLocaleDateString()}
 </div>
 )}
 </div>

 {/* Actions */}
 <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
 {canDownload && (
 <a
 href={reportsApi.downloadUrl(job.id)}
 download
 style={{ ...STYLES.btn('primary'), textDecoration: 'none', display: 'inline-block' }}
 >
 ↓ Download PDF
 </a>
 )}
 {canDelete && (
 <button onClick={onDelete} style={STYLES.btn('danger')}>✕</button>
 )}
 </div>
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════════════════
export default function ReportsPage() {
 const [reports, setReports] = useState<ReportJob[]>([]);
 const [loading, setLoading] = useState(true);
 const pollerRef = useRef<ReturnType<typeof setInterval> | null>(null);

 const load = async () => {
 try {
 const data = await reportsApi.list();
 setReports(data.reports);
 } finally { setLoading(false); }
 };

 // Auto-poll if any jobs are in progress
 useEffect(() => {
 load();
 pollerRef.current = setInterval(() => {
 const hasInProgress = reports.some(r => r.status === 'queued' || r.status === 'generating');
 if (hasInProgress) load();
 }, 3000);
 return () => { if (pollerRef.current) clearInterval(pollerRef.current); };
 }, []);

 // Re-check after every load if we should keep polling
 useEffect(() => {
 const hasInProgress = reports.some(r => r.status === 'queued' || r.status === 'generating');
 if (!hasInProgress && pollerRef.current) {
 // Slow down polling to 15s when nothing active
 }
 }, [reports]);

 const handleDelete = async (id: string) => {
 if (!confirm('Delete this report?')) return;
 await reportsApi.delete(id);
 load();
 };

 return (
 <div style={STYLES.page}>
 <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>

 <div style={STYLES.heading}>Compliance Reports</div>
 <div style={STYLES.sub}>
 One-click PDF generation for FDA FSMA 204, EUDR, Supplier Risk, and Recall documentation
 </div>

 <NewReportForm onCreated={load} />

 <div style={{ ...STYLES.card, padding: 0, overflow: 'hidden' }}>
 <div style={{ padding: '16px 20px', borderBottom: '1px solid #1a1815', display: 'flex', justifyContent: 'space-between' }}>
 <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
 Report History
 </div>
 <button onClick={load} style={STYLES.btn('ghost')}>↻ Refresh</button>
 </div>

 {loading ? (
 <div style={STYLES.empty}>Loading…</div>
 ) : reports.length === 0 ? (
 <div style={STYLES.empty}>
 No reports yet. Select a report type above to get started.
 </div>
 ) : (
 reports.map(job => (
 <ReportRow key={job.id} job={job} onDelete={() => { handleDelete(job.id); }} />
 ))
 )}
 </div>

 {/* Info callout */}
 <div style={{ ...STYLES.card, background: '#0a1520', borderColor: '#1a3a5c' }}>
 <div style={{ ...STYLES.label, color: '#5b9bd5', marginBottom: 8 }}>📋 About Compliance Reports</div>
 <p style={{ fontSize: 13, color: '#8aa8c0', lineHeight: 1.6, margin: 0 }}>
 Reports are generated asynchronously — typically ready in 20–60 seconds depending on data volume.
 Each report includes a TraceFull letterhead, AI-written narrative sections, data tables, and a signature block.
 PDFs are stored for 30 days then automatically purged.
 {' '}<strong style={{ color: '#5b9bd5' }}>FEATURE_REAL_AGENTS=true</strong> must be set for AI-generated narrative sections.
 </p>
 </div>
 </div>
 );
}
